import { IIconProps } from '@fluentui/react';
import { IAppContext } from 'App/AppContext';
import { TFunction } from 'i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import Dashboard from './dashboard';
import Language from './language';

export enum WidgetCategories {
  Task = 1,
  Risk = 2,
  Standard = 3,
  Library = 4,
  Process = 5,
  Microsoft = 6,
  KPI = 7,
  OrgUnit = 8,
  Partner = 9,
}

export enum WidgetTypes {
  NotSet = 0,
  MyUpcomingTasks = 1,
  MyOverdueTasks = 2,
  MyRecentItems = 8,
  MyActions = 9,
  SingleRisk = 20,
  SingleTask = 21,
  SingleKPI = 30,
  SingleKPILastPeriod = 31,
  RecentlyClosedTasks = 50,
  LibraryContent = 51,
  OpenTasksPerUser = 52,
  NormCoverage = 53,
  CustomNormCoverage = 54,
  RiskStatus = 55,
  RiskStatusNotAccepted = 56,
  MyNormCoverage = 57,
  CustomMyNormCoverage = 58,
  MicrosoftSecureScores = 59,
  TaskStatus = 60,
  LibraryReadResponse = 61,
  TaskTag = 62,
  LibraryReadStats = 63,
  TaskAction = 64,
  TenantRiskStatusOrgUnit = 65,
  TenantRiskStatusPartner = 66,
  TenantRiskChangesOrgUnit = 67,
  RiskOwner = 68,
  TenantRiskOwnerOrgUnit = 69,
  TenantControlStatus = 70,
  TenantControlChangesOrgUnit = 71,
  TenantListApprovalStatusOrgUnit = 72,
  TenantTaskStatusOrgUnit = 73,
  TenantUserStatsOrgUnit = 74,
  LibraryMyItems = 75,
  LibraryReadResponseAudiences = 76,
  MyCurrentTasks = 77,
  PolicyCoverage = 78,
}

export enum WidgetSizes {
  Small = 1,
  Medium = 2,
  Large = 4,
}

export const WidgetWidth: Record<string, number> = {
  [WidgetSizes.Small]: 300,
  [WidgetSizes.Medium]: 642,
  [WidgetSizes.Large]: 1326,
};

export interface IWidgetCustomAction {
  name: string;
  iconProps?: IIconProps;
  disabled?: boolean;
  callback: (widget: Widget) => void;
}

export interface IWidgetCategory {
  id: WidgetCategories;
  name: string;
}

export interface IWidgetType {
  id: number;
  type: WidgetTypes;
  name: string;
  description: string;
  category: WidgetCategories;
  hasConfig: boolean;
  canAdjustSize: boolean;
  defaultSize: WidgetSizes;
  needSharedData: boolean;
}

export class WidgetTenantConfig {
  tenants: string[];

  constructor() {
    this.tenants = [];
  }

  loadTenant(newRawConfig: WidgetTenantConfig | undefined) {
    if (newRawConfig) {
      try {
        this.tenants = newRawConfig.tenants ?? [];
      } catch {
        //ignore
      }
    }
  }

  cloneTenant(): WidgetTenantConfig {
    const newConfig = new WidgetTenantConfig();
    newConfig.tenants = [...this.tenants];

    return newConfig;
  }
}

export function getWidgetCategories(appContext: IAppContext, t: TFunction<string[]>) {
  const widgetCategories: IWidgetCategory[] = [];

  const widgetCategoriesTask: IWidgetCategory = {
    id: WidgetCategories.Task,
    name: t('widgets:Categories.Task'),
  };
  widgetCategories.push(widgetCategoriesTask);

  const widgetCategoriesRisk: IWidgetCategory = {
    id: WidgetCategories.Risk,
    name: t('widgets:Categories.Risk'),
  };
  widgetCategories.push(widgetCategoriesRisk);

  const widgetCategoriesNorm: IWidgetCategory = {
    id: WidgetCategories.Standard,
    name: t('widgets:Categories.Norm'),
  };
  widgetCategories.push(widgetCategoriesNorm);

  const widgetCategoriesLibrary: IWidgetCategory = {
    id: WidgetCategories.Library,
    name: t('widgets:Categories.Library'),
  };
  widgetCategories.push(widgetCategoriesLibrary);

  if (hasUserFeature(appContext, FeatureTypes.KPIsAndForms)) {
    const widgetCategoriesKPI: IWidgetCategory = {
      id: WidgetCategories.KPI,
      name: t('widgets:Categories.KPI'),
    };
    widgetCategories.push(widgetCategoriesKPI);
  }

  if (hasUserFeature(appContext, FeatureTypes.Automation)) {
    const widgetCategoriesMicrosoft: IWidgetCategory = {
      id: WidgetCategories.Microsoft,
      name: t('widgets:Categories.Microsoft'),
    };
    widgetCategories.push(widgetCategoriesMicrosoft);
  }

  if (hasUserFeature(appContext, FeatureTypes.OrgUnits)) {
    const widgetCategoriesTenantOU: IWidgetCategory = {
      id: WidgetCategories.OrgUnit,
      name: t('widgets:Categories.TenantOU'),
    };
    widgetCategories.push(widgetCategoriesTenantOU);
  }

  if (hasUserFeature(appContext, FeatureTypes.PartnerAdmin)) {
    const widgetCategoriesTenantPartner: IWidgetCategory = {
      id: WidgetCategories.Partner,
      name: t('widgets:Categories.TenantPartner'),
    };
    widgetCategories.push(widgetCategoriesTenantPartner);
  }

  return widgetCategories;
}

export function getWidgetTypes(appContext: IAppContext, t: TFunction<string[]>) {
  const widgetTypes: IWidgetType[] = [];

  const widgetTypeMyUpcomingTasks: IWidgetType = {
    id: 1,
    type: WidgetTypes.MyUpcomingTasks,
    name: t('widgets:MyUpcomingTasks.Name'),
    description: t('widgets:MyUpcomingTasks.Description'),
    category: WidgetCategories.Task,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: true,
  };
  widgetTypes.push(widgetTypeMyUpcomingTasks);

  const widgetTypeLibraryContent: IWidgetType = {
    id: 2,
    type: WidgetTypes.LibraryContent,
    name: t('widgets:LibraryContent.Name'),
    description: t('widgets:LibraryContent.Description'),
    category: WidgetCategories.Library,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetTypeLibraryContent);

  const widgetTypeMyOverdueTasks: IWidgetType = {
    id: 3,
    type: WidgetTypes.MyOverdueTasks,
    name: t('widgets:MyOverdueTasks.Name'),
    description: t('widgets:MyOverdueTasks.Description'),
    category: WidgetCategories.Task,
    hasConfig: false,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: true,
  };
  widgetTypes.push(widgetTypeMyOverdueTasks);

  const widgetTypeRecentlyClosedTasks: IWidgetType = {
    id: 4,
    type: WidgetTypes.RecentlyClosedTasks,
    name: t('widgets:RecentlyClosedTasks.Name'),
    description: t('widgets:RecentlyClosedTasks.Description'),
    category: WidgetCategories.Task,
    hasConfig: false,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: true,
  };
  widgetTypes.push(widgetTypeRecentlyClosedTasks);

  const widgetTypeSingleRisk: IWidgetType = {
    id: 5,
    type: WidgetTypes.SingleRisk,
    name: t('widgets:SingleRisk.Name'),
    description: t('widgets:SingleRisk.Description'),
    category: WidgetCategories.Risk,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetTypeSingleRisk);

  const widgetOpenTasksPerUser: IWidgetType = {
    id: 6,
    type: WidgetTypes.OpenTasksPerUser,
    name: t('widgets:OpenTasksPerUser.Name'),
    description: t('widgets:OpenTasksPerUser.Description'),
    category: WidgetCategories.Task,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetOpenTasksPerUser);

  const widgetNormCoverage: IWidgetType = {
    id: 7,
    type: WidgetTypes.NormCoverage,
    name: t('widgets:NormCoverage.Name'),
    description: t('widgets:NormCoverage.Description'),
    category: WidgetCategories.Standard,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetNormCoverage);

  const widgetCustomNormCoverage: IWidgetType = {
    id: 8,
    type: WidgetTypes.CustomNormCoverage,
    name: t('widgets:CustomNormCoverage.Name'),
    description: t('widgets:CustomNormCoverage.Description'),
    category: WidgetCategories.Standard,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetCustomNormCoverage);

  const widgetRiskStatus: IWidgetType = {
    id: 9,
    type: WidgetTypes.RiskStatus,
    name: t('widgets:RiskStatus.Name'),
    description: t('widgets:RiskStatus.Description'),
    category: WidgetCategories.Risk,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetRiskStatus);

  const widgetRiskStatusNotAccepted: IWidgetType = {
    id: 10,
    type: WidgetTypes.RiskStatusNotAccepted,
    name: t('widgets:RiskStatusNotAccepted.Name'),
    description: t('widgets:RiskStatusNotAccepted.Description'),
    category: WidgetCategories.Risk,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetRiskStatusNotAccepted);

  const widgetMyNormCoverage: IWidgetType = {
    id: 11,
    type: WidgetTypes.MyNormCoverage,
    name: t('widgets:MyNormCoverage.Name'),
    description: t('widgets:MyNormCoverage.Description'),
    category: WidgetCategories.Standard,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetMyNormCoverage);

  const widgetCustomMyNormCoverage: IWidgetType = {
    id: 12,
    type: WidgetTypes.CustomMyNormCoverage,
    name: t('widgets:CustomMyNormCoverage.Name'),
    description: t('widgets:CustomMyNormCoverage.Description'),
    category: WidgetCategories.Standard,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetCustomMyNormCoverage);

  const widgetSingleTask: IWidgetType = {
    id: 13,
    type: WidgetTypes.SingleTask,
    name: t('widgets:SingleTask.Name'),
    description: t('widgets:SingleTask.Description'),
    category: WidgetCategories.Task,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetSingleTask);

  const widgetMicrosoftSecureScores: IWidgetType = {
    id: 13,
    type: WidgetTypes.MicrosoftSecureScores,
    name: t('widgets:MicrosoftSecureScores.Name'),
    description: t('widgets:MicrosoftSecureScores.Description'),
    category: WidgetCategories.Microsoft,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetMicrosoftSecureScores);

  const widgetTaskStatus: IWidgetType = {
    id: 15,
    type: WidgetTypes.TaskStatus,
    name: t('widgets:TaskStatus.Name'),
    description: t('widgets:TaskStatus.Description'),
    category: WidgetCategories.Task,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetTaskStatus);

  const widgetSingleKPI: IWidgetType = {
    id: 16,
    type: WidgetTypes.SingleKPI,
    name: t('widgets:SingleKPI.Name'),
    description: t('widgets:SingleKPI.Description'),
    category: WidgetCategories.KPI,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetSingleKPI);

  const widgetSingleKPILastPeriod: IWidgetType = {
    id: 17,
    type: WidgetTypes.SingleKPILastPeriod,
    name: t('widgets:SingleKPILastPeriod.Name'),
    description: t('widgets:SingleKPILastPeriod.Description'),
    category: WidgetCategories.KPI,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetSingleKPILastPeriod);

  if (hasUserFeature(appContext, FeatureTypes.ReadConfirmations)) {
    const LibraryReadResponseAudiences: IWidgetType = {
      id: 18,
      type: WidgetTypes.LibraryReadResponseAudiences,
      name: t('widgets:LibraryReadResponseAudiences.Name'),
      description: t('widgets:LibraryReadResponseAudiences.Description'),
      category: WidgetCategories.Library,
      hasConfig: false,
      canAdjustSize: true,
      defaultSize: WidgetSizes.Medium,
      needSharedData: false,
    };
    widgetTypes.push(LibraryReadResponseAudiences);
  }

  if (hasUserFeature(appContext, FeatureTypes.ReadConfirmations)) {
    const widgetLibraryReadResponse: IWidgetType = {
      id: 19,
      type: WidgetTypes.LibraryReadResponse,
      name: t('widgets:LibraryReadResponse.Name'),
      description: t('widgets:LibraryReadResponse.Description'),
      category: WidgetCategories.Library,
      hasConfig: true,
      canAdjustSize: true,
      defaultSize: WidgetSizes.Medium,
      needSharedData: false,
    };
    widgetTypes.push(widgetLibraryReadResponse);
  }

  const widgetTaskTag: IWidgetType = {
    id: 20,
    type: WidgetTypes.TaskTag,
    name: t('widgets:TaskTag.Name'),
    description: t('widgets:TaskTag.Description'),
    category: WidgetCategories.Task,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetTaskTag);

  const widgetLibraryReadStats: IWidgetType = {
    id: 21,
    type: WidgetTypes.LibraryReadStats,
    name: t('widgets:LibraryReadStats.Name'),
    description: t('widgets:LibraryReadStats.Description'),
    category: WidgetCategories.Library,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetLibraryReadStats);

  const widgetTaskAction: IWidgetType = {
    id: 22,
    type: WidgetTypes.TaskAction,
    name: t('widgets:TaskAction.Name'),
    description: t('widgets:TaskAction.Description'),
    category: WidgetCategories.Task,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetTaskAction);

  const widgetTenantRiskStatusOrgUnit: IWidgetType = {
    id: 23,
    type: WidgetTypes.TenantRiskStatusOrgUnit,
    name: t('widgets:TenantRiskStatusOrgUnit.Name'),
    description: t('widgets:TenantRiskStatusOrgUnit.Description'),
    category: WidgetCategories.OrgUnit,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Medium,
    needSharedData: true,
  };
  widgetTypes.push(widgetTenantRiskStatusOrgUnit);

  const widgetTenantRiskStatusPartner: IWidgetType = {
    id: 24,
    type: WidgetTypes.TenantRiskStatusPartner,
    name: t('widgets:TenantRiskStatusPartner.Name'),
    description: t('widgets:TenantRiskStatusPartner.Description'),
    category: WidgetCategories.Partner,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Medium,
    needSharedData: true,
  };
  widgetTypes.push(widgetTenantRiskStatusPartner);

  const widgetTenantRiskChangesOrgUnit: IWidgetType = {
    id: 25,
    type: WidgetTypes.TenantRiskChangesOrgUnit,
    name: t('widgets:TenantRiskChangesOrgUnit.Name'),
    description: t('widgets:TenantRiskChangesOrgUnit.Description'),
    category: WidgetCategories.OrgUnit,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Medium,
    needSharedData: true,
  };
  widgetTypes.push(widgetTenantRiskChangesOrgUnit);

  const widgetRiskOwner: IWidgetType = {
    id: 26,
    type: WidgetTypes.RiskOwner,
    name: t('widgets:RiskOwner.Name'),
    description: t('widgets:RiskOwner.Description'),
    category: WidgetCategories.Risk,
    hasConfig: true,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetRiskOwner);

  const widgetTenantRiskOwnerOrgUnit: IWidgetType = {
    id: 27,
    type: WidgetTypes.TenantRiskOwnerOrgUnit,
    name: t('widgets:TenantRiskOwnerOrgUnit.Name'),
    description: t('widgets:TenantRiskOwnerOrgUnit.Description'),
    category: WidgetCategories.OrgUnit,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Medium,
    needSharedData: true,
  };
  widgetTypes.push(widgetTenantRiskOwnerOrgUnit);

  const widgetTenantControlStatus: IWidgetType = {
    id: 28,
    type: WidgetTypes.TenantControlStatus,
    name: t('widgets:TenantControlStatus.Name'),
    description: t('widgets:TenantControlStatus.Description'),
    category: WidgetCategories.OrgUnit,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Medium,
    needSharedData: true,
  };
  widgetTypes.push(widgetTenantControlStatus);

  const widgetTenantControlChangesOrgUnit: IWidgetType = {
    id: 29,
    type: WidgetTypes.TenantControlChangesOrgUnit,
    name: t('widgets:TenantControlChangesOrgUnit.Name'),
    description: t('widgets:TenantControlChangesOrgUnit.Description'),
    category: WidgetCategories.OrgUnit,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Medium,
    needSharedData: true,
  };
  widgetTypes.push(widgetTenantControlChangesOrgUnit);

  const widgetTenantListApprovalStatus: IWidgetType = {
    id: 30,
    type: WidgetTypes.TenantListApprovalStatusOrgUnit,
    name: t('widgets:TenantListApprovalStatusOrgUnit.Name'),
    description: t('widgets:TenantListApprovalStatusOrgUnit.Description'),
    category: WidgetCategories.OrgUnit,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Medium,
    needSharedData: true,
  };
  widgetTypes.push(widgetTenantListApprovalStatus);

  const widgetTenantTaskStatus: IWidgetType = {
    id: 31,
    type: WidgetTypes.TenantTaskStatusOrgUnit,
    name: t('widgets:TenantTaskStatusOrgUnit.Name'),
    description: t('widgets:TenantTaskStatusOrgUnit.Description'),
    category: WidgetCategories.OrgUnit,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Medium,
    needSharedData: true,
  };
  widgetTypes.push(widgetTenantTaskStatus);

  const widgetTenantUserStats: IWidgetType = {
    id: 32,
    type: WidgetTypes.TenantUserStatsOrgUnit,
    name: t('widgets:WidgetTenantUserStatsOrgUnit.Name'),
    description: t('widgets:WidgetTenantUserStatsOrgUnit.Description'),
    category: WidgetCategories.OrgUnit,
    hasConfig: true,
    canAdjustSize: true,
    defaultSize: WidgetSizes.Medium,
    needSharedData: true,
  };
  widgetTypes.push(widgetTenantUserStats);

  const widgetLibraryMyItems: IWidgetType = {
    id: 33,
    type: WidgetTypes.LibraryMyItems,
    name: t('widgets:WidgetLibraryMyItems.Name'),
    description: t('widgets:WidgetLibraryMyItems.Description'),
    category: WidgetCategories.Library,
    hasConfig: false,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetLibraryMyItems);

  const widgetTypeMyCurrentTasks: IWidgetType = {
    id: 34,
    type: WidgetTypes.MyCurrentTasks,
    name: t('widgets:MyCurrentTasks.Name'),
    description: t('widgets:MyCurrentTasks.Description'),
    category: WidgetCategories.Task,
    hasConfig: false,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: true,
  };
  widgetTypes.push(widgetTypeMyCurrentTasks);

  const widgetPolicyCoverage: IWidgetType = {
    id: 35,
    type: WidgetTypes.PolicyCoverage,
    name: t('widgets:PolicyCoverage.Name'),
    description: t('widgets:PolicyCoverage.Description'),
    category: WidgetCategories.Standard,
    hasConfig: false,
    canAdjustSize: false,
    defaultSize: WidgetSizes.Small,
    needSharedData: false,
  };
  widgetTypes.push(widgetPolicyCoverage);

  return widgetTypes;
}

export default class Widget {
  widgetId: number;

  dashboardId: number;

  name: string;

  widgetType: WidgetTypes;

  widgetConfig?: string;

  widgetSize: WidgetSizes;

  sortOrder: number;

  trans: Widget_Translation[];

  transIdx: number;

  //Internal props

  customActions?: IWidgetCustomAction[];

  dashboard?: Dashboard;

  type?: IWidgetType;

  constructor() {
    this.widgetId = 0;
    this.dashboardId = 0;
    this.name = '';
    this.widgetType = WidgetTypes.NotSet;
    this.sortOrder = -1;
    this.trans = [];
    this.transIdx = -1;
    this.widgetSize = WidgetSizes.Small;
  }

  getName(appContext: IAppContext, t: TFunction): string {
    const existingTranslation = this.trans.length > 0 ? this.trans[0].name : undefined;
    const existingName = this.name && this.name !== '' ? this.name : undefined;
    const typeName = getWidgetTypes(appContext, t).find((w) => w.type === this.widgetType)?.name ?? '';
    const name = existingTranslation ?? existingName ?? typeName;

    return name;
  }

  getType(widgetTypes: IWidgetType[]): IWidgetType | undefined {
    return widgetTypes.find((w) => w.type === this.widgetType);
  }

  clone(): Widget {
    const newWidget = new Widget();
    newWidget.widgetId = this.widgetId;
    newWidget.dashboardId = this.dashboardId;
    newWidget.dashboard = this.dashboard;
    newWidget.widgetConfig = this.widgetConfig;
    newWidget.name = this.name;
    newWidget.widgetType = this.widgetType;
    newWidget.sortOrder = this.sortOrder;
    newWidget.trans = [...this.trans];
    newWidget.transIdx = this.transIdx;
    newWidget.widgetSize = this.widgetSize;

    return newWidget;
  }
}

export class Widget_Translation {
  widgetId: number;

  languageId: number;

  name: string;

  lang?: Language;

  constructor() {
    this.widgetId = 0;
    this.languageId = 0;
    this.name = '';
  }
}
